import {
  IconAlertTriangle,
  IconCheck,
  IconChecks,
  IconClockPause,
  TablerIconsProps,
} from '@tabler/icons-react';
import { ClassMeta } from 'hooks/class/useClassMeta';

import { AthleteClassState } from './useAthleteClassState';

export interface AthleteClassMeta {
  reserveAction: 'reserve' | 'joinWaitlist' | null;
  cancelAction: 'cancelReservation' | 'lateCancelReservation' | 'leaveWaitlist' | null;
  color: string;
  backgroundColor: string;
  Icon?: (props: TablerIconsProps) => JSX.Element;
  stateText: string | null;
  supplementalText: string | null;
}

export default function useAthleteClassMeta(
  athleteClassState: AthleteClassState,
  classMeta: ClassMeta,
  waitlistOrder?: number,
): AthleteClassMeta {
  if (athleteClassState === 'noRelation') {
    const noRelationDefaults = {
      reserveAction: null,
      cancelAction: null,
      color: 'dimmed',
      backgroundColor: 'white',
      stateText: null,
      supplementalText: null,
    } as const;

    if (classMeta.hasStarted || !classMeta.isWithinReservationWindow) {
      return { ...noRelationDefaults, stateText: 'Reservation Closed' };
    }

    if (!classMeta.isAtCapacity) {
      const spotsRemaining = classMeta.capacity - classMeta.inClassCount;
      return {
        ...noRelationDefaults,
        reserveAction: 'reserve',
        supplementalText: `(${spotsRemaining} Spot${spotsRemaining === 1 ? '' : 's'} Remaining)`,
      };
    }

    if (!classMeta.isWaitlistAtCapacity) {
      return { ...noRelationDefaults, reserveAction: 'joinWaitlist' };
    }

    return { ...noRelationDefaults, stateText: 'Event at Capacity' };
  }

  if (athleteClassState === 'waitlisted') {
    return {
      reserveAction: null,
      cancelAction: classMeta.hasStarted ? null : 'leaveWaitlist',
      color: 'kiloBlue.7',
      backgroundColor: 'white',
      Icon: IconClockPause,
      stateText: 'Waitlisted',
      supplementalText: waitlistOrder ? `(You're #${waitlistOrder} on the waitlist)` : null,
    };
  }

  if (athleteClassState === 'reserved') {
    let cancelAction: AthleteClassMeta['cancelAction'] = null;
    if (classMeta.isWithinCancellationWindow) {
      cancelAction = 'cancelReservation';
    } else if (classMeta.isWithinLateCancellationWindow) {
      cancelAction = 'lateCancelReservation';
    }

    return {
      reserveAction: null,
      cancelAction,
      color: 'green.7',
      backgroundColor: 'green.0',
      Icon: IconCheck,
      stateText: 'Reserved',
      supplementalText: null,
    };
  }

  if (athleteClassState === 'reservedUncredited') {
    return {
      reserveAction: null,
      cancelAction: classMeta.isWithinCancellationWindow ? 'cancelReservation' : null,
      color: 'kiloBlue.7',
      backgroundColor: 'white',
      Icon: IconCheck,
      stateText: 'Reserved',
      supplementalText: '(Uncredited)',
    };
  }

  if (athleteClassState === 'reservedAndCheckedIn' || athleteClassState === 'notReservedButCheckedIn') {
    return {
      reserveAction: null,
      cancelAction: null,
      color: 'green.7',
      backgroundColor: 'green.1',
      Icon: IconChecks,
      stateText: 'Checked In',
      supplementalText: null,
    };
  }

  if (athleteClassState === 'lateCancelWithCreditDeduction') {
    return {
      reserveAction: null,
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'Late Cancel',
      supplementalText: '(Credit deducted)',
    };
  }

  if (athleteClassState === 'lateCancelWithoutCreditDeduction') {
    return {
      reserveAction: null,
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'Late Cancel',
      supplementalText: null,
    };
  }

  if (athleteClassState === 'noShowWithCreditDeduction') {
    return {
      reserveAction: null,
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'No Show',
      supplementalText: '(Credit deducted)',
    };
  }

  if (athleteClassState === 'noShowWithoutCreditDeduction') {
    return {
      reserveAction: null,
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'No Show',
      supplementalText: null,
    };
  }

  return {
    reserveAction: null,
    cancelAction: null,
    color: 'dimmed',
    backgroundColor: 'white',
    stateText: null,
    supplementalText: null,
  };
}
